import { Box, css, Typography } from '@mui/material';
import Decimal from 'decimal.js';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MinusIcon } from 'src/assets/svg/minus.svg';
import { ReactComponent as PlusIcon } from 'src/assets/svg/plus.svg';
import { Section } from 'src/components/section';
import { Select } from 'src/components/select';
import { Currency } from 'src/shared/enum/Currency';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { InstalmentType } from 'src/shared/enum/InstalmentType';
import { Term } from 'src/shared/types/booking-types';
import { getTheme } from 'src/utils/getTheme';
import { formatDate } from 'src/utils/format-date';
import { getCurrencySymbol } from 'src/utils/get-currency-symbol';
import { createLabelFromInstalmentType } from 'src/utils/instalment-type-to-name-convert';

import { styles } from './styles';
import { BookingDetailsSkeleton } from './booking-details-skeleton';

import type { BookingDetailsViewProps } from './types';

export const BookingDetailsView: FC<BookingDetailsViewProps> = ({
    restStyles,
    roomData,
    bookingDetails,
    userChoices,
    handleTermChange,
    handleInstalmentChange,
    incrementStudentNumber,
    decrementStudentNumber,
    setNumberOfStudents,
    loading,
    isBookingDetailsDialog,
}) => {
    const { t } = useTranslation();
    const { minStudents, maxStudents } = bookingDetails
        ? bookingDetails.options.students
        : { minStudents: 1, maxStudents: 1 };
    const { numberOfStudents, term: selectedTerm, instalmentType } = userChoices;
    const { theme, isHosTheme } = getTheme();

    const termName = selectedTerm && selectedTerm.name;
    const monthlyPrice = roomData && roomData.monthly_price;
    const PRICE_PER_WEEK_LABEL =
        !bookingDetails.isStl && bookingDetails.isMonthly
            ? t('booking_summary_per_month_payment')
            : t('booking_summary_per_week_payment');

    useEffect(() => {
        const { terms, options } = bookingDetails;

        if (terms && terms.length > 0 && !selectedTerm) {
            handleTermChange(terms[0]);
        }

        if (selectedTerm) {
            const selectedTermDetails = terms.find((term) => term.name === selectedTerm.name);

            if (selectedTermDetails) {
                const instalmentsExist =
                    selectedTermDetails.instalments && selectedTermDetails.instalments.length > 0;

                if (instalmentsExist && !instalmentType) {
                    handleInstalmentChange(
                        selectedTermDetails.instalments[0].name as InstalmentType,
                        selectedTermDetails.instalments[0].id
                    );
                } else if (!instalmentsExist) {
                    handleInstalmentChange(null, null);
                }
            } else {
                handleTermChange(terms[0]);
                handleInstalmentChange(
                    terms[0].instalments[0].name as InstalmentType,
                    terms[0].instalments[0].id
                );
            }
        }

        const { students } = options;
        if (students) {
            if (!numberOfStudents || numberOfStudents < minStudents) {
                setNumberOfStudents(minStudents);
            } else if (numberOfStudents > maxStudents) {
                setNumberOfStudents(maxStudents);
            }
        }
    }, []);

    if (loading) return <BookingDetailsSkeleton />;

    const getTermTitle = (count: number) => {
        if (monthlyPrice && !bookingDetails.isStl) {
            return t('booking_summary_months_count', {
                count,
            });
        }
        return t('booking_summary_weeks_count', {
            count,
        });
    };

    const getInstalmentOptions = () => {
        if (!bookingDetails) throw new Error(ErrorMessage.NO_BOOKING_DETAILS_PROVIDED);

        // Fetch the currency symbol using the CurrencySymbol mapping
        const currencySymbol = getCurrencySymbol(bookingDetails.currency as Currency);

        const selectedTermDetails = bookingDetails.terms.find((term) => term.name === termName);
        if (!selectedTermDetails || !selectedTermDetails.instalments) return [];

        return selectedTermDetails.instalments.map((instalment) => {
            const selectedAmount = instalment.amounts.find(
                (amt) => amt.students === numberOfStudents
            );

            const weeklyPrice = selectedAmount
                ? new Decimal(selectedAmount.weeklyPrice)
                : new Decimal(0);
            const price = selectedAmount ? new Decimal(selectedAmount.price) : new Decimal(0);
            const displayPrice = bookingDetails.isStl ? weeklyPrice : price;

            const instalmentLabel = createLabelFromInstalmentType(instalment.name, t);
            const pricePwLabel = PRICE_PER_WEEK_LABEL;

            return {
                id: instalment.id,
                value: instalment.name,
                title: `${currencySymbol}${displayPrice.toFixed(2)}${pricePwLabel}`,
                subtitle: instalmentLabel,
            };
        });
    };

    const getIconStyles = (studentsCount: number, type: 'increment' | 'decrement') => {
        if (type === 'increment') {
            return studentsCount < maxStudents ? styles.activeIcon : styles.disabledIcon;
        }
        return studentsCount > minStudents ? styles.activeIcon : styles.disabledIcon;
    };

    const getIconColor = (studentsCount: number, type: 'increment' | 'decrement') => {
        const activeColor = isHosTheme
            ? theme.palette.secondary.dark
            : theme.palette.text.secondary;
        const disabledColor = theme.palette.text.disabled;

        if (type === 'increment') {
            return studentsCount < maxStudents ? activeColor : disabledColor;
        }
        return studentsCount > minStudents ? activeColor : disabledColor;
    };

    return (
        <Section header={t('booking_summary_booking_details_title')} withHeaderStipe={!isHosTheme}>
            <Box css={[styles.wrapper(isBookingDetailsDialog), restStyles]}>
                <Box css={styles.studentNumber}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_student_number_subtitle')}
                    </Typography>
                    <Box css={styles.counter}>
                        <Box
                            css={css(
                                styles.iconWrapper,
                                getIconStyles(numberOfStudents, 'decrement')
                            )}
                            onClick={decrementStudentNumber}
                            id="booking-details-decrement-students"
                        >
                            <MinusIcon color={getIconColor(numberOfStudents, 'decrement')} />
                        </Box>
                        <Typography variant="h4">{numberOfStudents}</Typography>
                        <Box
                            css={css(
                                styles.iconWrapper,
                                getIconStyles(numberOfStudents, 'increment')
                            )}
                            onClick={incrementStudentNumber}
                            id="booking-details-increment-students"
                        >
                            <PlusIcon color={getIconColor(numberOfStudents, 'increment')} />
                        </Box>
                    </Box>
                </Box>
                <Box css={styles.bookingLengthWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_length_subtitle')}
                    </Typography>
                    <Box css={styles.bookingLength}>
                        {bookingDetails.terms.map((term: Term, index) => (
                            <Select
                                key={term.name}
                                value={term.name}
                                isSelected={termName === term.name}
                                onClick={() => handleTermChange(term)}
                                title={getTermTitle(term.duration)}
                                subtitle={`${formatDate(term.startDate)} - ${formatDate(term.endDate)}`}
                                id={`term-${index + 1}`}
                            />
                        ))}
                    </Box>
                </Box>
                <Box css={styles.instalmentPlanWrapper}>
                    <Typography variant="h4">
                        {t('booking_summary_booking_details_instalment_plan_subtitle')}
                    </Typography>
                    <Box css={styles.instalmentPlan}>
                        {getInstalmentOptions().map(({ value, title, subtitle, id }, index) => (
                            <Select
                                key={value}
                                value={value}
                                isSelected={instalmentType === value}
                                onClick={() => handleInstalmentChange(value, id)}
                                title={title}
                                subtitle={subtitle}
                                id={`option-${index + 1}`}
                            />
                        ))}
                    </Box>
                </Box>
            </Box>
        </Section>
    );
};
